/**
 * Subscription Monetization Interface - Upgrade Confirmation Modal
 * 
 * Must include:
 * - Plan change summary (from → to)
 * - Immediate activation notice
 * - No proration disclosure
 * - Next billing amount
 * 
 * Goal: Clear expectations to minimize billing disputes.
 */
import React, { useState } from 'react';
import { formatDateLocale } from '../../utils/dateFormat';
import { useTranslation } from 'react-i18next';
import { useClinicData } from '../../context/ClinicDataContext';
import { useClinicScope } from '../../hooks/useClinicScope';
import { useContent } from '../../hooks/useContent';
import { getLocalizedField } from '../../context/LanguageContext';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent } from '../ui/card';
import { 
  ArrowRight, AlertCircle, CheckCircle, X, Loader2,
  Zap, Crown, Building2, Clock, CreditCard, CalendarDays,
  Shield, Info
} from 'lucide-react';
import api from '../../utils/api';
import CommercialDeltaPanel from './CommercialDeltaPanel';

// PXC-B.6 — show the live commercial delta panel inside the upgrade modal.
const PXC_B6_QUOTA_DELTA_UX = true;

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

const UpgradeConfirmationModal = ({
  isOpen,
  onClose,
  fromPackage,
  toPackage,
  onConfirm,
}) => {
  const { t } = useTranslation();
  const { clinicUser } = useClinicData();
  const { clinicId: rbacClinicId } = useClinicScope();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  
  if (!isOpen || !fromPackage || !toPackage) return null;
  
  // Package styling
  const packageConfig = {
    TRIAL: { icon: Clock, color: 'bg-blue-100 text-blue-700', gradient: 'from-blue-500 to-blue-600' },
    STARTER: { icon: Zap, color: 'bg-green-100 text-green-700', gradient: 'from-green-500 to-green-600' },
    PRO: { icon: Crown, color: 'bg-purple-100 text-purple-700', gradient: 'from-purple-500 to-purple-600' },
    ENTERPRISE: { icon: Building2, color: 'bg-amber-100 text-amber-700', gradient: 'from-amber-500 to-amber-600' },
  };
  
  const fromConfig = packageConfig[fromPackage.id || fromPackage] || packageConfig.TRIAL;
  const toConfig = packageConfig[toPackage.id || toPackage.package_key] || packageConfig.STARTER;
  const FromIcon = fromConfig.icon;
  const ToIcon = toConfig.icon;
  
  // Get package details
  const fromName = getLocalizedField(fromPackage.name, 'en') || fromPackage.displayName || fromPackage;
  const toName = getLocalizedField(toPackage.name, 'en') || toPackage.package_name;
  const toAmount = toPackage.amount ?? toPackage.pricing?.amount ?? null;
  const toCurrency = toPackage.currency || toPackage.pricing?.currency || 'EUR';
  const toBillingInterval = toPackage.billing_interval || toPackage.pricing?.interval || 'month';
  // ZERO-PRICE-UPGRADE-03 — recognise a published numeric zero even when the
  // API serialises it as a numeric string ("0", "0.00"). A missing, blank or
  // non-numeric price is NOT zero: it stays "pricing unavailable" and blocks.
  const parsedAmount = (() => {
    if (typeof toAmount === 'number') return Number.isFinite(toAmount) ? toAmount : null;
    if (typeof toAmount === 'string' && toAmount.trim() !== '') {
      const n = Number(toAmount);
      return Number.isFinite(n) ? n : null;
    }
    return null;
  })();
  const hasPrice = parsedAmount !== null;
  const isZeroPrice = hasPrice && parsedAmount === 0;
  
  // Handle upgrade confirmation
  const handleConfirm = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const clinicId = rbacClinicId || clinicUser?.clinicId;
      const clinicEmail = clinicUser?.email || '';
      const packageKey = toPackage.id || toPackage.package_key;
      
      if (!clinicId) {
        setError('Clinic ID not found. Please re-login.');
        setIsLoading(false);
        return;
      }

      if (!clinicEmail) {
        setError('Clinic email not found. Please re-login.');
        setIsLoading(false);
        return;
      }
      
      // Step 1: Try upgrade endpoint (requires active subscription)
      let data;
      try {
        const upgradeRes = await api.post(`/subscription/clinic/${clinicId}/upgrade`, {
          new_package_key: packageKey,
          clinic_email: clinicEmail,
          origin_url: window.location.origin,
        });
        data = upgradeRes.data;
      } catch (upgradeErr) {
        // If no active subscription, fall back to checkout flow
        const errMsg = upgradeErr.response?.data?.detail || upgradeErr.message || '';
        const isNoSubscription = errMsg.toLowerCase().includes('no active subscription');
        
        if (!isNoSubscription) {
          throw new Error(errMsg || 'Upgrade failed');
        }
        
        // Fall back to checkout session (new subscription)
        const checkoutRes = await api.post('/subscription/checkout/session', {
          package_id: packageKey,
          clinic_id: clinicId,
          clinic_email: clinicEmail,
          clinic_name: clinicUser?.clinicName || '',
          origin_url: window.location.origin,
        });
        data = checkoutRes.data;
      }
      
      // Redirect to Stripe checkout or notify success
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        onConfirm?.(data);
        onClose?.();
      }
      
    } catch (err) {
      console.error('[UpgradeModal] Error:', err);
      setError(err.response?.data?.detail || err.message || 'An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    // MOBILE-MODAL-SCROLL-01 — iPhone Safari could not scroll this modal: the
    // panel had no max-height and `overflow-hidden`, so a centred card taller
    // than the viewport was clipped at BOTH ends and the confirm button was
    // unreachable. Now: the overlay itself scrolls (`items-start` on mobile so
    // nothing is clipped even where `dvh` is unsupported), the panel is capped
    // to the *dynamic* viewport height, and the body is the scroll container
    // with the header kept outside it.
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-start sm:items-center justify-center overflow-y-auto overscroll-contain p-0 sm:p-4"
      data-testid="upgrade-confirmation-overlay"
    >
      <div
        className="bg-white shadow-2xl w-full sm:max-w-lg rounded-none sm:rounded-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200 flex flex-col max-h-[100dvh] sm:max-h-[90vh] sm:my-auto"
        data-testid="upgrade-confirmation-panel"
      >
        {/* Header — outside the scroll area so it stays reachable */}
        <div className={`flex-shrink-0 bg-gradient-to-r ${toConfig.gradient} px-6 py-4 text-white`}>
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold flex items-center gap-2">
              <ToIcon className="h-5 w-5" />
              {t('upgrade.confirmTitle', 'Confirm Upgrade')}
            </h2>
            <button 
              onClick={onClose}
              className="p-1 hover:bg-white/20 rounded-lg transition-colors"
              data-testid="close-upgrade-confirmation"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
        
        <div
          className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-6 space-y-5 pb-[max(1.5rem,env(safe-area-inset-bottom))] [-webkit-overflow-scrolling:touch]"
          data-testid="upgrade-confirmation-body"
        >
          {/* Plan Change Summary */}
          <div className="flex items-center justify-center gap-4">
            <div className="text-center">
              <div className={`w-14 h-14 mx-auto rounded-xl ${fromConfig.color} flex items-center justify-center mb-2`}>
                <FromIcon className="h-7 w-7" />
              </div>
              <p className="text-sm font-medium text-gray-600">{fromName}</p>
            </div>
            
            <ArrowRight className="h-8 w-8 text-gray-400" />
            
            <div className="text-center">
              <div className={`w-14 h-14 mx-auto rounded-xl bg-gradient-to-r ${toConfig.gradient} text-white flex items-center justify-center mb-2 ring-4 ring-offset-2 ${toConfig.color.replace('text-', 'ring-').replace('bg-', 'ring-')}`}>
                <ToIcon className="h-7 w-7" />
              </div>
              <p className="text-sm font-bold text-gray-900">{toName}</p>
            </div>
          </div>

          {/* PXC-B.6 — Commercial delta panel */}
          {PXC_B6_QUOTA_DELTA_UX && (
            <CommercialDeltaPanel
              fromPackage={fromPackage}
              toPackage={toPackage}
              mode="upgrade"
            />
          )}
          
          {/* Important Notices */}
          <div className="space-y-3">
            {/* Immediate Activation Notice */}
            <Card className="bg-green-50 border-green-200">
              <CardContent className="p-4 flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-green-800">
                    {t('upgrade.immediateActivation', 'Immediate Activation')}
                  </p>
                  <p className="text-sm text-green-700">
                    {isZeroPrice
                      ? t('upgrade.immediateActivationFreeDesc', 'Your new plan features and quotas are available immediately. No payment is required.')
                      : t('upgrade.immediateActivationDesc', 'Your new plan features and quotas will be available immediately after payment.')}
                  </p>
                </div>
              </CardContent>
            </Card>
            
            {/* No Proration Disclosure */}
            <Card className="bg-amber-50 border-amber-200">
              <CardContent className="p-4 flex items-start gap-3">
                <Info className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-amber-800">
                    {t('upgrade.noProration', 'No Proration')}
                  </p>
                  <p className="text-sm text-amber-700">
                    {t('upgrade.noProrationDesc', 'No credit will be applied for your current plan. Your new billing cycle starts immediately.')}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
          
          {/* Billing / plan summary */}
          <Card className="border-2 border-gray-200">
            <CardContent className="p-4">
              <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                {isZeroPrice ? <CheckCircle className="h-4 w-4" /> : <CreditCard className="h-4 w-4" />}
                {isZeroPrice
                  ? t('upgrade.planSummary', 'Plan Summary')
                  : t('upgrade.billingSummary', 'Billing Summary')}
              </h4>
              
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">{t('upgrade.newPlan', 'New Plan')}</span>
                  <span className="font-medium">{toName}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">{t('upgrade.billingCycle', 'Billing Cycle')}</span>
                  <span className="font-medium capitalize">{toBillingInterval}</span>
                </div>
                <div className="border-t pt-2 mt-2 flex justify-between items-center gap-3">
                  <span className="font-semibold text-gray-800">
                    {isZeroPrice
                      ? t('upgrade.paymentLabel', 'Payment')
                      : t('upgrade.chargeNow', 'Charge Today')}
                  </span>
                  <span
                    className={isZeroPrice
                      ? 'font-bold text-base text-green-700 text-right'
                      : (hasPrice ? 'font-bold text-xl text-gray-900' : 'text-sm font-medium text-amber-600 text-right')}
                    data-testid="upgrade-charge-today"
                  >
                    {isZeroPrice
                      ? t('upgrade.noPaymentRequiredShort', 'No payment required')
                      : hasPrice
                        ? `${toCurrency === 'EUR' ? '€' : '$'}${parsedAmount}`
                        : t('packageSelection.priceUnavailableCta', 'Pricing unavailable')}
                  </span>
                </div>
                {/* A future payment date would be misleading for a free plan. */}
                {!isZeroPrice && hasPrice && (
                  <div className="flex justify-between text-sm text-gray-500">
                    <span>{t('upgrade.nextBilling', 'Next Billing')}</span>
                    <span className="flex items-center gap-1">
                      <CalendarDays className="h-3 w-3" />
                      {formatDateLocale(Date.now() + 30 * 24 * 60 * 60 * 1000)}
                    </span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
          
          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800 flex items-center gap-2">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {error}
            </div>
          )}
          
          {/* Action Buttons */}
          <div className="flex gap-3">
            <Button 
              variant="outline" 
              className="flex-1"
              onClick={onClose}
              disabled={isLoading}
            >
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button 
              className={`flex-1 bg-gradient-to-r ${toConfig.gradient} hover:opacity-90`}
              onClick={handleConfirm}
              disabled={isLoading || !hasPrice}
              data-testid="confirm-upgrade-btn"
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t('upgrade.processing', 'Processing...')}
                </>
              ) : (
                <>
                  {isZeroPrice
                    ? <CheckCircle className="h-4 w-4 mr-2" />
                    : <CreditCard className="h-4 w-4 mr-2" />}
                  {isZeroPrice
                    ? t('upgrade.confirmUpgradeFree', 'Confirm & Activate')
                    : t('upgrade.confirmUpgrade', 'Confirm & Pay')}
                </>
              )}
            </Button>
          </div>
          
          {/* Security Note */}
          <p className="text-center text-xs text-gray-500 flex items-center justify-center gap-1">
            <Shield className="h-3 w-3" />
            {isZeroPrice
              ? t('upgrade.noPaymentRequired', 'No payment required for this plan.')
              : t('upgrade.securePayment', 'Secure payment powered by Stripe')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default UpgradeConfirmationModal;
